import { MetaPixelEvent, MetaPixelEventType } from '../types/metaPixel';

// Meta Pixel ID from the provided code
const META_PIXEL_ID = '1417518489322657';

/**
 * Check if Meta Pixel is loaded and available
 */
export const isMetaPixelLoaded = (): boolean => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Initialize Meta Pixel if not already initialized
 */
export const initializeMetaPixel = (): void => {
  if (typeof window === 'undefined') return;
  
  if (!window.fbq) {
    console.warn('Meta Pixel not loaded');
    return;
  }

  try {
    window.fbq('init', META_PIXEL_ID);
    console.log('Meta Pixel initialized with ID:', META_PIXEL_ID);
  } catch (error) {
    console.error('Error initializing Meta Pixel:', error);
  }
};

/**
 * Track a standard Meta Pixel event
 */
export const trackMetaPixelEvent = (
  eventName: MetaPixelEventType,
  parameters?: Record<string, any>
): void => {
  if (!isMetaPixelLoaded()) {
    console.warn('Meta Pixel not loaded, cannot track event:', eventName);
    return;
  }

  try {
    window.fbq('track', eventName, parameters);
    console.log('Meta Pixel event tracked:', eventName, parameters);
  } catch (error) {
    console.error('Error tracking Meta Pixel event:', error);
  }
};

/**
 * Track a custom Meta Pixel event
 */
export const trackCustomMetaPixelEvent = (
  eventName: string,
  parameters?: Record<string, any>
): void => {
  if (!isMetaPixelLoaded()) {
    console.warn('Meta Pixel not loaded, cannot track custom event:', eventName);
    return;
  }

  try {
    window.fbq('trackCustom', eventName, parameters);
    console.log('Meta Pixel custom event tracked:', eventName, parameters);
  } catch (error) {
    console.error('Error tracking Meta Pixel custom event:', error);
  }
};

/**
 * Track page view
 */
export const trackPageView = (): void => {
  trackMetaPixelEvent('PageView');
};

/**
 * Track lead generation
 */
export const trackLead = (parameters?: {
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
}): void => {
  trackMetaPixelEvent('Lead', parameters);
};

/**
 * Track contact form submission
 */
export const trackContact = (parameters?: {
  content_name?: string;
  content_category?: string;
}): void => {
  trackMetaPixelEvent('Contact', parameters);
};

/**
 * Track content view
 */
export const trackViewContent = (parameters?: {
  content_type?: string;
  content_ids?: string[];
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
}): void => {
  trackMetaPixelEvent('ViewContent', parameters);
};

/**
 * Track search
 */
export const trackSearch = (parameters?: {
  search_string?: string;
  content_category?: string;
}): void => {
  trackMetaPixelEvent('Search', parameters);
};

/**
 * Track contractor profile view
 */
export const trackContractorView = (contractorId: string, contractorName: string): void => {
  trackViewContent({
    content_type: 'contractor',
    content_ids: [contractorId],
    content_name: contractorName,
    content_category: 'contractor_profile'
  });
};

/**
 * Track service request form submission
 */
export const trackServiceRequest = (serviceType: string, value?: number): void => {
  trackLead({
    content_name: `Service Request - ${serviceType}`,
    content_category: 'service_request',
    value: value,
    currency: 'USD'
  });
};

/**
 * Track contractor search
 */
export const trackContractorSearch = (searchTerm: string, resultsCount: number): void => {
  trackSearch({
    search_string: searchTerm,
    content_category: 'contractor_search'
  });
  
  // Also track as custom event with results count
  trackCustomMetaPixelEvent('ContractorSearch', {
    search_string: searchTerm,
    results_count: resultsCount
  });
};

/**
 * Track newsletter subscription
 */
export const trackNewsletterSubscription = (): void => {
  trackMetaPixelEvent('Subscribe', {
    content_name: 'Newsletter Subscription',
    content_category: 'email_signup'
  });
};

/**
 * Track exit intent popup interaction
 */
export const trackExitIntent = (action: 'viewed' | 'subscribed' | 'dismissed'): void => {
  trackCustomMetaPixelEvent('ExitIntent', {
    action: action,
    content_name: 'Exit Intent Popup',
    content_category: 'user_engagement'
  });
};
